import { useEffect, useState } from 'react';
import { get, post, del, uploadFile, errorMessage } from '../api';
import { navigate } from '../router';
import { Field, Modal, Spinner } from '../ui/controls';
import { toast } from '../ui/toast';
import { thumbUrl } from '../assets';
import { CATALOG } from '@cal/engine';
import { NewCalendar } from './NewCalendar';

export interface ProjectRow { id: string; name: string; customerId: string | null; customerName: string | null; year: number; status: string; version: number; approvalCurrent: boolean; approvedVersion: number | null; thumbnailAssetId: string | null; updatedAt: string; updatedByName: string | null; productType?: 'monthly' | 'year' | 'tent' | null; catalogId?: string | null; archived?: boolean }
export interface Customer { id: string; name: string; email: string; phone: string; notes: string; projectCount?: number }

const TYPE_LABEL = { monthly: 'Monthly', year: 'Year planner', tent: 'Tent' } as const;
const productLabel = (p: ProjectRow) => CATALOG.find((c) => c.id === p.catalogId)?.name ?? (p.productType ? `${TYPE_LABEL[p.productType]} (custom)` : '');

const STATUS: Record<string, [string, string]> = { draft: ['Draft', ''], proof_generated: ['Proof sent', 'info'], changes_requested: ['Changes requested', 'warn'], approved: ['Approved', 'ok'], exported: ['Exported', 'ok'] };

export function Dashboard() {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<ProjectRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [dup, setDup] = useState<ProjectRow | null>(null);
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [archived, setArchived] = useState(false);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const ctl = new AbortController();
    const t = setTimeout(() => get<{ projects: ProjectRow[] }>(`/api/projects?q=${encodeURIComponent(q)}${archived ? '&archived=1' : ''}`, ctl.signal).then((r) => { setRows(r.projects); setErr(null); }).catch((e) => e.name !== 'AbortError' && setErr(errorMessage(e))), 200);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [q, archived, reload]);
  const shown = rows?.filter((p) => (!status || p.status === status) && (!type || p.productType === type)) ?? null;
  const archive = async (p: ProjectRow) => {
    try {
      if (p.archived) { await post(`/api/projects/${p.id}/restore`, {}); toast(`Restored “${p.name}”`, 'ok'); }
      else { await del(`/api/projects/${p.id}`); toast(`Archived “${p.name}”. Tick “Archived” to see or restore it.`, 'ok'); }
      setReload((n) => n + 1);
    } catch (e) { toast(errorMessage(e), 'err'); }
  };
  return (
    <div className="page-scroll"><div className="page col" style={{ gap: 16 }}>
      <div className="row wrap">
        <h1 className="grow">Calendar projects</h1>
        <label className="btn">Import backup<input type="file" accept=".zip" hidden onChange={async (e) => {
          const f = e.target.files?.[0]; if (!f) return;
          try { const r = await uploadFile<{ id: string }>('/api/projects/import', f); toast('Project imported', 'ok'); navigate(`/projects/${r.id}`); } catch (x) { toast(errorMessage(x), 'err'); }
        }} /></label>
        <button className="btn primary" onClick={() => setCreating(true)}>+ New calendar</button>
      </div>
      <div className="row wrap" style={{ gap: 8 }}>
        <input className="input" style={{ maxWidth: 360, flex: '1 1 220px' }} type="search" placeholder="Search by project, customer or year…" aria-label="Search projects" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="input" style={{ width: 'auto' }} aria-label="Filter by status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {Object.entries(STATUS).map(([k, [label]]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <select className="input" style={{ width: 'auto' }} aria-label="Filter by product type" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All products</option>
          {Object.entries(TYPE_LABEL).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <label className="check small"><input type="checkbox" checked={archived} onChange={(e) => setArchived(e.target.checked)} />Archived</label>
      </div>
      {err && <div className="notice err">{err}</div>}
      {!rows && !err && <Spinner label="Loading projects…" />}
      {rows && rows.length > 0 && shown?.length === 0 && <p className="muted">No projects match these filters.</p>}
      {rows && rows.length === 0 && archived && <p className="muted">No archived projects{q ? ' match your search' : ''}.</p>}
      {rows && rows.length === 0 && !archived && (
        <div className="card empty">
          <h2>{q ? 'No projects match your search' : 'No calendar projects yet'}</h2>
          <p>{q ? `Try a customer name or a year such as ${new Date().getFullYear() + 1}.` : 'Create the first calendar: choose a size and template, add photographs and holidays, then export a proof.'}</p>
          {!q && <button className="btn primary" onClick={() => setCreating(true)}>Create a calendar</button>}
        </div>
      )}
      {shown && shown.length > 0 && (
        <div className="proj-grid">
          {shown.map((p) => (
            <div key={p.id} className="proj-card">
              <button style={{ border: 0, padding: 0, background: 'none', cursor: 'pointer', textAlign: 'left' }} onClick={() => navigate(`/projects/${p.id}`)} aria-label={`Open ${p.name}`}>
                <div className="proj-thumb" style={p.thumbnailAssetId ? { backgroundImage: `url(${thumbUrl(p.thumbnailAssetId)})` } : undefined}>{!p.thumbnailAssetId && 'No photos yet'}</div>
                <div className="proj-meta">
                  <strong>{p.name}</strong>
                  {productLabel(p) && <span className="small">{productLabel(p)}</span>}
                  <span className="small muted">{p.customerName ?? 'No customer'} · {p.year}</span>
                  <span className="row small"><span className={`badge ${STATUS[p.status]?.[1] ?? ''}`}>{STATUS[p.status]?.[0] ?? p.status}</span><span className="faint">rev {p.version} · {new Date(p.updatedAt).toLocaleDateString()}</span></span>
                </div>
              </button>
              <div className="row" style={{ padding: '0 12px 10px' }}>
                <button className="btn sm" onClick={() => setDup(p)}>Duplicate…</button>
                <span className="spacer" />
                <button className="btn sm ghost" onClick={() => archive(p)}>{p.archived ? 'Restore' : 'Archive'}</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {creating && <NewCalendar onClose={() => setCreating(false)} />}
      {dup && <DuplicateDialog p={dup} onClose={() => setDup(null)} />}
    </div></div>
  );
}

export function DuplicateDialog({ p, onClose }: { p: ProjectRow; onClose: () => void }) {
  const [name, setName] = useState(`${p.name.replace(/\b(19|20|21)\d\d\b/, String(p.year + 1))}${p.name.includes(String(p.year)) ? '' : ` ${p.year + 1}`}`);
  const [year, setYear] = useState(p.year + 1);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState<{ id: string; notes: string[] } | null>(null);
  if (notes) return (
    <Modal title="Review before continuing" onClose={() => navigate(`/projects/${notes.id}`)} footer={<button className="btn primary" onClick={() => navigate(`/projects/${notes.id}`)}>Open the new project</button>}>
      <p>Dates were recalculated for {year}. Photographs, styles and recurring events were kept.</p>
      <ul>{notes.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
    </Modal>
  );
  return (
    <Modal title={`Duplicate “${p.name}”`} onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" disabled={busy || !name.trim()} onClick={async () => {
      setBusy(true);
      try {
        const r = await post<{ id: string; reviewNotes: string[] }>(`/api/projects/${p.id}/duplicate`, { name, year });
        if (r.reviewNotes.length) setNotes({ id: r.id, notes: r.reviewNotes }); else navigate(`/projects/${r.id}`);
      } catch (e) { toast(errorMessage(e), 'err'); setBusy(false); }
    }}>Duplicate</button></>}>
      <Field label="New project name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      <Field label="Year" hint={year !== p.year ? 'Dates are recalculated. Year-specific holidays are removed so you can choose the new year’s verified data.' : 'Same year: an exact copy.'}>
        <input className="input" type="number" min={1900} max={2100} value={year} onChange={(e) => setYear(+e.target.value)} />
      </Field>
    </Modal>
  );
}

